import { inject, Injectable } from '@angular/core';
import { BaseService } from '../../core/services/base.httpService';
import { combineLatestWith, filter, map, Observable, of, take, tap } from 'rxjs';
import { URLConstants } from '../../shared/utility/url-constants';
import {
  AgentCreatePayload,
  AgentFilter,
  AgentGrid,
  AgentInfo,
  AgentRow,
} from '../components/agents/agent.model';
import { handleErrorMessage } from '../../shared/operators/error.operator';
import {
  ActionIcons,
  AttachmentDownloadRequestType,
  BulkDeleteItemNames,
  DownloadItemNames,
  GridColumnsStorageNames,
  ModalPopupMessages,
  popupWidth,
  SingleDeleteHeaderText,
  StatusCodes,
} from '../../shared/utility/constants';
import { SnakbarService } from '../../shared/services/snakbar.service';
import { ModalService } from '../../shared/services/modal.service';
import { CommonService } from '../../shared/services/common.service';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { AgentColumnList } from '../../shared/components/tables/tables.config';
import {
  agentExcelHeaders,
  agentExcelColumnList,
  agentInfoAddressHeaders,
  agentInfoHeader,
  agentInfoSectionHeaderKeys,
  agentInfoSectionHeaders,
  agentsColumnConfig,
} from '../components/agents/agent.config';
import { PrintService } from '../../shared/services/print.service';
import { CommonPopupService } from '../../shared/services/common-popup.service';
import {
  IExcelInfoPageData,
  TableColumns,
} from '../../shared/models/dynamic.model';
import { DownloadFileService } from '../../shared/services/download-file.service';
import { AgentSelector } from '../components/agents/store';
import { Store } from '@ngxs/store';

@Injectable({ providedIn: 'root' })
export class AgentService {
  private readonly baseService = inject(BaseService);
  private readonly errorHandler = handleErrorMessage();
  private readonly snackBarService: SnakbarService = inject(SnakbarService);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly commonService: CommonService = inject(CommonService);
  private readonly printService: PrintService = inject(PrintService);
  private readonly store: Store = inject(Store);
  private readonly fileService: DownloadFileService =
    inject(DownloadFileService);
  private readonly commonPopupService: CommonPopupService =
    inject(CommonPopupService);

  public getSearchData(event: AgentFilter): Observable<AgentGrid> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.agents.search, event, '', 'json')
      .pipe(map(({ data }) => data), this.errorHandler());
  }

  public getById(id: string | number): Observable<AgentInfo> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        `${URLConstants.agents.getById}/${id}`,
        { id },
        '',
        'json'
      )
      .pipe(
        map(({ data }) => data?.[0]),
        this.errorHandler()
      );
  }

  public create(agent: AgentCreatePayload): Observable<number> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.agents.create, agent, 'json')
      .pipe(
        tap(data => {
          if (data.statusCode === StatusCodes.Success) {
            this.snackBarService.openCustomisedSnackBar(
              data.message,
              ModalPopupMessages.success
            );
          }
        }),
        // Returns the newly created ID of agent
        map(data => data?.data.agent.id),
        this.errorHandler()
      );
  }

  public update(agent: Omit<AgentInfo, 'agentTypes'>): Observable<unknown> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.agents.update, agent, 'json')
      .pipe(
        tap(data => {
          if (data.statusCode === StatusCodes.Success) {
            this.snackBarService.openCustomisedSnackBar(
              data.message,
              ModalPopupMessages.success
            );
          }
        }),
        this.errorHandler()
      );
  }

  public deleteWithConfirmation(selectedAgents: AgentRow[]): Observable<void> {
    const message =
      selectedAgents.length > 1
        ? ModalPopupMessages.bulkDeleteMessage(
            selectedAgents.length,
            BulkDeleteItemNames.Agents
          )
        : ModalPopupMessages.singleDeleteMessage(
            SingleDeleteHeaderText.Agent
          ).replace('?', '') + `${selectedAgents[0].name} ?`;

    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isFooterRequired: true,
        isBulk: selectedAgents.length > 1,
        isMultiple: true,
      },
      selectedAgents.length > 1
        ? ModalPopupMessages.BulkDeleteHeader
        : ModalPopupMessages.singleDeleteHeader(SingleDeleteHeaderText.Agent),
      MessagePopupComponent,
      popupWidth.deletePopup
    );

    return dialogRef.afterClosed().pipe(filter(data => !!data));
  }

  public delete(ids: number[]): Observable<unknown> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.agents.delete, { ids }, 'json')
      .pipe(
        tap(data => {
          if (data.statusCode === StatusCodes.Success) {
            this.snackBarService.openCustomisedSnackBar(
              ids.length > 1
                ? ModalPopupMessages.bulkDeleteSuccessMessage(
                    ids.length,
                    BulkDeleteItemNames.Agents
                  )
                : ModalPopupMessages.singleDeleteSuccessMessage(
                    SingleDeleteHeaderText.Agent
                  ),
              ModalPopupMessages.success
            );
          }
        }),
        this.errorHandler()
      );
  }

  public download(selectedRows: AgentRow[], event: AgentFilter) {
    const dialogRef = this.modalService.openPopup(
      {
        isForDownload: true,
        isBulk: false,
        isFooterRequired: true,
      },
      ActionIcons.download,
      MessagePopupComponent,
      popupWidth.downloadPopup
    );
    const agents$ = selectedRows.length
      ? of(selectedRows)
      : this.getSearchData(event).pipe(map(response => response.agents));
    return dialogRef.afterClosed().pipe(
      filter(data => !!data?.popupClose),
      combineLatestWith(agents$),
      tap(([data, rows]) => {
        const fileType = data.fileType;
        const rowData = rows.map(row => {
          return {
            code: row.code,
            name: row.name,
            status: row.status,
            types: row.types,
          };
        });

        this.commonPopupService.setRequiredDataForDownload(
          AttachmentDownloadRequestType.agent,
          agentExcelHeaders,
          agentExcelColumnList,
          fileType,
          selectedRows.length > 1
            ? DownloadItemNames.Agents
            : AttachmentDownloadRequestType.agent,
          rowData,
          GridColumnsStorageNames.agent
        );
      })
    );
  }

  public print(selectedRows: AgentRow[], event: AgentFilter) {
    const agents$ = selectedRows.length
      ? of(selectedRows)
      : this.getSearchData(event).pipe(map(response => response?.agents));

    this.commonPopupService.openPrintPopup(
      this.modalService,
      SingleDeleteHeaderText.Agent,
      structuredClone(agentsColumnConfig) as TableColumns[],
      selectedRows.length > 0,
      true
    );

    return this.printService.isSetDataForPreviewTable.pipe(
      filter(response => !!Object.keys(response).length),
      combineLatestWith(agents$),
      tap(([response, agents]) => {
        this.commonService.modifiedDataForPrint(
          agents,
          response,
          SingleDeleteHeaderText.Agent,
          structuredClone(AgentColumnList)
        );
      }),
      take(1)
    );
  }

  handleAgentInfoDownload(agentName: string) {
    const dialogRef = this.commonPopupService.openDownloadPopup(
      this.modalService
    );

    dialogRef
      .afterClosed()
      .pipe(filter((data: any) => data?.popupClose), take(1))
      .subscribe((data: any) => {
        const excelData: IExcelInfoPageData = {
          name: AttachmentDownloadRequestType.agent,
          sectionNames: agentInfoSectionHeaders,
          sectionHeaderKeys: agentInfoSectionHeaderKeys,
          pageData: this.prepareAgentInfoDataForPrint(),
          selectedFileType: data.fileType,
          successMessage: AttachmentDownloadRequestType.agent,
          isFromSummary: false,
          extOrNumber: agentName,
        };
        this.fileService.generateExcelCSVFile(excelData);
      });
  }

  handleAgentInfoPrint() {
    this.commonPopupService.openPrintPopup(
      this.modalService,
      SingleDeleteHeaderText.Agent,
      this.prepareAgentInfoDataForPrint() as any,
      false,
      false
    );
  }

  private prepareAgentInfoDataForPrint() {
    const data = this.store.selectSnapshot(AgentSelector.getAgentInfo);
    const agentTypes = this.store.selectSnapshot(AgentSelector.getAgentTypes);
    const agentAssignedTypes = agentTypes
      .filter(agType => data.agent.types.includes(agType.id))
      .map(agType => `${agType.agentType} - ${agType.name}`)
      .join(', ');

    return {
      agentInfo: {
        data: [
          {
            ...data.agent,
            status: data.agent.status ? 'Active' : 'Inactive',
            types: agentAssignedTypes,
          },
        ],
        headers: agentInfoHeader.map(({ label }) => label),
        headerKeys: agentInfoHeader.map(({ key }) => key),
        config: agentInfoHeader,
      },
      agentAddress: {
        data: [
          {
            countryCode:
              data.address.countryCode && data.address.countryName
                ? `${data.address.countryCode} - ${data.address.countryName}`
                : data.address.countryCode,
            state:
              data.address.state && data.address.stateName
                ? `${data.address.state} - ${data.address.stateName}`
                : data.address.state,
            companyName: data.address.companyName,
            address1: data.address.address1,
            address2: data.address.address2,
            city: data.address.city,
            zip: data.address.zip,
            phone: data.address.phone,
            email: data.address.email,
          },
        ],
        headers: agentInfoAddressHeaders.map(({ label }) => label),
        headerKeys: agentInfoAddressHeaders.map(({ key }) => key),
        config: agentInfoAddressHeaders,
      },
    };
  }
}
